export type Language = 'en' | 'zh-CN' | 'zh-TW' | 'ms' | 'ta';

export const LANGUAGE_LABELS: Record<Language, string> = {
  'en': 'English',
  'zh-CN': '简体中文',
  'zh-TW': '繁體中文',
  'ms': 'Bahasa Melayu',
  'ta': 'தமிழ்',
};

export type TranslationKeys = typeof translations['en'];

export const translations: Record<Language, Record<string, string>> = {
  en: {
    // Greetings
    goodMorning: 'Good morning',
    goodAfternoon: 'Good afternoon',
    goodEvening: 'Good evening',

    // Weather
    singapore: 'Singapore',

    // Pinned Location
    pinnedLocations: 'Pinned Locations',
    deletePinnedLocation: 'Delete Pinned Location?',
    removePinnedBody: 'Remove the current pinned location for',
    locationRemoved: 'Location Removed',

    // Level selector
    whereDidYouPark: 'Where did you park today?',

    // Entry form
    lotPlaceholder: 'Lot / Space number  —  A5, 23B, P4...',
    notesPlaceholder: 'Notes  —  near elevator, next to pillar, close to exit...',
    saveLocation: 'Save Location',
    photoAttached: 'Photo attached',

    // Camera
    takePhoto: 'Take Photo',
    retake: 'Retake',
    usePhoto: 'Use Photo',
    cameraUnavailable: 'Camera Unavailable',
    photoPreview: 'Photo Preview',
    photo: 'Photo',

    // Records
    recentRecords: 'Recent Records',
    clearAll: 'Clear all',
    noRecordsYet: 'No records yet',
    clearAllRecords: 'Clear All Records?',
    permanentlyDelete: 'Permanently delete all records for',
    cannotBeUndone: 'Cannot be undone.',
    recordsCleared: 'Records Cleared',
    deleteAll: 'Delete All',

    // Toasts
    addDetails: 'Add Details',
    selectLevelPrompt: 'Select a level, lot number, or notes.',
    locationSaved: 'Location Saved !',

    // Common
    cancel: 'Cancel',
    delete: 'Delete',
    close: 'Close',
    back: 'Back',
    edit: 'Edit',
    lot: 'Lot',

    // Profile menu
    home: 'Home',
    faq: 'FAQ',
    settings: 'Settings',
    appSettings: 'App Settings',
    activeProfile: 'Active Profile',
    manageProfiles: 'Manage Profiles',
    language: 'Language',
    weatherRegion: 'Weather Region',
    addToHomeScreen: 'Add to Home Screen',
  },

  'zh-CN': {
    goodMorning: '早上好',
    goodAfternoon: '下午好',
    goodEvening: '晚上好',

    singapore: '新加坡',

    pinnedLocations: '已标记位置',
    deletePinnedLocation: '删除标记位置？',
    removePinnedBody: '删除当前标记位置',
    locationRemoved: '位置已删除',

    whereDidYouPark: '今天停在哪里？',

    lotPlaceholder: '车位号 — A5, 23B, P4...',
    notesPlaceholder: '备注 — 靠近电梯、柱子旁、接近出口...',
    saveLocation: '保存位置',
    photoAttached: '已附照片',

    takePhoto: '拍照',
    retake: '重拍',
    usePhoto: '使用照片',
    cameraUnavailable: '相机不可用',
    photoPreview: '照片预览',
    photo: '照片',

    recentRecords: '最近记录',
    clearAll: '清除全部',
    noRecordsYet: '暂无记录',
    clearAllRecords: '清除所有记录？',
    permanentlyDelete: '永久删除所有记录',
    cannotBeUndone: '此操作不可撤销。',
    recordsCleared: '记录已清除',
    deleteAll: '全部删除',

    addDetails: '添加详情',
    selectLevelPrompt: '请选择楼层、车位号或备注。',
    locationSaved: '位置已保存！',

    cancel: '取消',
    delete: '删除',
    close: '关闭',
    back: '返回',
    edit: '编辑',
    lot: '车位',

    home: '首页',
    faq: '常见问题',
    settings: '设置',
    appSettings: '应用设置',
    activeProfile: '当前档案',
    manageProfiles: '管理档案',
    language: '语言',
    weatherRegion: '天气地区',
    addToHomeScreen: '添加到主屏幕',
  },

  'zh-TW': {
    goodMorning: '早安',
    goodAfternoon: '午安',
    goodEvening: '晚安',

    singapore: '新加坡',

    pinnedLocations: '已標記位置',
    deletePinnedLocation: '刪除標記位置？',
    removePinnedBody: '刪除當前標記位置',
    locationRemoved: '位置已刪除',

    whereDidYouPark: '今天停在哪裡？',

    lotPlaceholder: '車位號 — A5, 23B, P4...',
    notesPlaceholder: '備註 — 靠近電梯、柱子旁、接近出口...',
    saveLocation: '儲存位置',
    photoAttached: '已附照片',

    takePhoto: '拍照',
    retake: '重拍',
    usePhoto: '使用照片',
    cameraUnavailable: '相機不可用',
    photoPreview: '照片預覽',
    photo: '照片',

    recentRecords: '最近紀錄',
    clearAll: '清除全部',
    noRecordsYet: '暫無紀錄',
    clearAllRecords: '清除所有紀錄？',
    permanentlyDelete: '永久刪除所有紀錄',
    cannotBeUndone: '此操作不可撤銷。',
    recordsCleared: '紀錄已清除',
    deleteAll: '全部刪除',

    addDetails: '新增詳情',
    selectLevelPrompt: '請選擇樓層、車位號或備註。',
    locationSaved: '位置已儲存！',

    cancel: '取消',
    delete: '刪除',
    close: '關閉',
    back: '返回',
    edit: '編輯',
    lot: '車位',

    home: '首頁',
    faq: '常見問題',
    settings: '設定',
    appSettings: '應用設定',
    activeProfile: '目前檔案',
    manageProfiles: '管理檔案',
    language: '語言',
    weatherRegion: '天氣地區',
    addToHomeScreen: '新增至主畫面',
  },

  ms: {
    goodMorning: 'Selamat pagi',
    goodAfternoon: 'Selamat petang',
    goodEvening: 'Selamat malam',

    singapore: 'Singapura',

    pinnedLocations: 'Lokasi Ditanda',
    deletePinnedLocation: 'Padam lokasi ditanda?',
    removePinnedBody: 'Padam lokasi ditanda semasa untuk',
    locationRemoved: 'Lokasi dipadam',

    whereDidYouPark: 'Di mana anda letak kereta hari ini?',

    lotPlaceholder: 'No. petak — A5, 23B, P4...',
    notesPlaceholder: 'Nota — dekat lif, sebelah tiang, dekat pintu keluar...',
    saveLocation: 'Simpan Lokasi',
    photoAttached: 'Foto dilampirkan',

    takePhoto: 'Ambil Foto',
    retake: 'Ambil Semula',
    usePhoto: 'Guna Foto',
    cameraUnavailable: 'Kamera tidak tersedia',
    photoPreview: 'Pratonton Foto',
    photo: 'Foto',

    recentRecords: 'Rekod Terkini',
    clearAll: 'Padam semua',
    noRecordsYet: 'Tiada rekod lagi',
    clearAllRecords: 'Padam semua rekod?',
    permanentlyDelete: 'Padam semua rekod secara kekal untuk',
    cannotBeUndone: 'Tidak boleh dibatalkan.',
    recordsCleared: 'Rekod dipadam',
    deleteAll: 'Padam Semua',

    addDetails: 'Tambah Butiran',
    selectLevelPrompt: 'Pilih aras, nombor petak, atau nota.',
    locationSaved: 'Lokasi Disimpan!',

    cancel: 'Batal',
    delete: 'Padam',
    close: 'Tutup',
    back: 'Kembali',
    edit: 'Ubah',
    lot: 'Petak',

    home: 'Utama',
    faq: 'Soalan Lazim',
    settings: 'Tetapan',
    appSettings: 'Tetapan Aplikasi',
    activeProfile: 'Profil Aktif',
    manageProfiles: 'Urus Profil',
    language: 'Bahasa',
    weatherRegion: 'Kawasan Cuaca',
    addToHomeScreen: 'Tambah ke Skrin Utama',
  },

  ta: {
    goodMorning: 'காலை வணக்கம்',
    goodAfternoon: 'மதிய வணக்கம்',
    goodEvening: 'மாலை வணக்கம்',

    singapore: 'சிங்கப்பூர்',

    pinnedLocations: 'குறிக்கப்பட்ட இடம்',
    deletePinnedLocation: 'குறிக்கப்பட்ட இடத்தை நீக்கவா?',
    removePinnedBody: 'தற்போதைய குறிக்கப்பட்ட இடத்தை நீக்கு',
    locationRemoved: 'இடம் நீக்கப்பட்டது',

    whereDidYouPark: 'இன்று எங்கே நிறுத்தினீர்கள்?',

    lotPlaceholder: 'நிறுத்த எண் — A5, 23B, P4...',
    notesPlaceholder: 'குறிப்புகள் — லிஃப்ட் அருகில், தூண் அருகில்...',
    saveLocation: 'இடத்தை சேமி',
    photoAttached: 'புகைப்படம் இணைக்கப்பட்டது',

    takePhoto: 'புகைப்படம் எடு',
    retake: 'மீண்டும் எடு',
    usePhoto: 'புகைப்படத்தைப் பயன்படுத்து',
    cameraUnavailable: 'கேமரா கிடைக்கவில்லை',
    photoPreview: 'புகைப்பட முன்னோட்டம்',
    photo: 'புகைப்படம்',

    recentRecords: 'சமீபத்திய பதிவுகள்',
    clearAll: 'அனைத்தையும் அழி',
    noRecordsYet: 'பதிவுகள் இல்லை',
    clearAllRecords: 'அனைத்து பதிவுகளையும் அழிக்கவா?',
    permanentlyDelete: 'அனைத்து பதிவுகளையும் நிரந்தரமாக நீக்கு',
    cannotBeUndone: 'மீளமுடியாது.',
    recordsCleared: 'பதிவுகள் அழிக்கப்பட்டன',
    deleteAll: 'அனைத்தையும் நீக்கு',

    addDetails: 'விவரங்களைச் சேர்',
    selectLevelPrompt: 'நிலை, இட எண், அல்லது குறிப்பைத் தேர்ந்தெடுக்கவும்.',
    locationSaved: 'இடம் சேமிக்கப்பட்டது!',

    cancel: 'ரத்து',
    delete: 'நீக்கு',
    close: 'மூடு',
    back: 'பின்',
    edit: 'திருத்து',
    lot: 'இடம்',

    home: 'முகப்பு',
    faq: 'கேள்விகள்',
    settings: 'அமைப்புகள்',
    appSettings: 'செயலி அமைப்புகள்',
    activeProfile: 'செயலில் உள்ள சுயவிவரம்',
    manageProfiles: 'சுயவிவரங்களை நிர்வகி',
    language: 'மொழி',
    weatherRegion: 'வானிலை பகுதி',
    addToHomeScreen: 'முகப்புத் திரையில் சேர்',
  },
};
